const API_BASE = '/api';

export type PersonType = 'official' | 'corporate' | 'executive' | 'ceo' | 'donor' | 'lobbyist' | 'media' | 'other';
export type OfficialLifecycleStatus = 'active' | 'vying' | 'inactive' | 'archived';
export type OfficialRaceStage = 'primary' | 'general';

export interface Official {
  id: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  person_type: PersonType;
  office_level: string | null;
  office_branch: string | null;
  office_name: string;
  chamber: string | null;
  state: string | null;
  district: string | null;
  party: string | null;
  organization: string | null;
  bio: string | null;
  photo_url: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  office_address: string | null;
  accountability_summary: string | null;
  accountability_score: number | null;
  accountability_confidence?: number | null;
  accountability_flags?: string | null;
  adoption_count?: number | null;
  source: string;
  source_id: string | null;
  bioguide_id: string | null;
  openstates_id: string | null;
  is_active: number;
  lifecycle_status?: OfficialLifecycleStatus;
  race_stage?: OfficialRaceStage | null;
  archived_at?: number | null;
  archive_reason?: string | null;
  replaced_by_official_id?: string | null;
  created_at: number;
  updated_at: number;
}

export interface OfficialSocialAccount {
  id: string;
  official_id: string;
  platform: string;
  handle: string;
  url: string | null;
  follower_count: number | null;
  is_verified: number;
}

export interface OfficialActivity {
  id: string;
  official_id: string;
  activity_type: string;
  title: string;
  description: string | null;
  source_url: string | null;
  activity_date: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface OfficialsListResponse {
  officials: Official[];
  pagination: Pagination;
}

export interface AccountabilityRecord {
  id: string;
  official_id: string;
  record_type: string;
  title: string;
  description: string | null;
  impact_type: string | null;
  evidence_url: string | null;
  record_date: number | null;
  created_at: number;
}

export interface ArchivedOfficialReference {
  id: string;
  name: string;
  office_name: string;
  party?: string | null;
  state?: string | null;
  photo_url?: string | null;
  lifecycle_status?: OfficialLifecycleStatus;
}

export interface ArchiveSnapshotSummary {
  id: string;
  official_id: string;
  archived_at: number;
  archive_reason: string | null;
  replaced_by_official_id: string | null;
  created_at: number;
}

export interface ArchiveSnapshotPayload {
  official: Official;
  socialAccounts: OfficialSocialAccount[];
  recentActivity: OfficialActivity[];
  accountabilityRecords: AccountabilityRecord[];
  dossierStatus: DossierStatus | null;
  intelligence: Record<string, unknown>;
  assets: any[];
  research: any[];
}

export interface ArchiveSnapshotRecord extends ArchiveSnapshotSummary {
  snapshot: ArchiveSnapshotPayload;
}

export interface OfficialArchiveHistoryResponse {
  officialId: string;
  archiveHistory: ArchiveSnapshotSummary[];
  latestArchiveSnapshot: ArchiveSnapshotRecord | null;
  replacedByOfficial: ArchivedOfficialReference | null;
}

export interface OfficialDetailResponse {
  official: Official;
  socialAccounts: OfficialSocialAccount[];
  recentActivity: OfficialActivity[];
  accountabilityRecords: AccountabilityRecord[];
  archiveHistory: ArchiveSnapshotSummary[];
  latestArchiveSnapshot: ArchiveSnapshotRecord | null;
  replacedByOfficial: ArchivedOfficialReference | null;
}

export interface CachedSocialIntel {
  [key: string]: unknown;
}

export interface CachedGeoImpact {
  [key: string]: unknown;
}

export interface CachedProfilePhoto {
  photoUrl?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  resolution?: string | null;
  licenseType?: string | null;
  [key: string]: unknown;
}

export interface OfficialDetailBundleResponse extends OfficialDetailResponse {
  dossierStatus: DossierStatus | null;
  intelligence: {
    briefing: CacheEntry<CachedBriefing>;
    voting_record: CacheEntry<CachedVotingRecord>;
    public_record: CacheEntry<CachedPublicRecord>;
    accountability_score: CacheEntry<CachedScore>;
    social_intel: CacheEntry<CachedSocialIntel>;
    geo_impact: CacheEntry<CachedGeoImpact>;
    profile_photo: CacheEntry<CachedProfilePhoto>;
    legislative_record: CacheEntry<CachedLegislativeRecord>;
    campaign_finance: CacheEntry<CachedCampaignFinance>;
  };
  assets: any[];
  research: any[];
  blueskyThread: BlueskyThreadData;
}

export interface StatsResponse {
  total: number;
  dossiersCompleted: number;
  byLevel: { office_level: string; count: number }[];
  byParty: { party: string; count: number }[];
  byType: { person_type: string; count: number }[];
}

export interface HomepageHeatItem {
  officialId: string;
  name: string;
  officeName: string;
  state: string | null;
  photoUrl: string | null;
  accountabilityScore: number | null;
  adoptionCount: number;
  eventType: 'score_change' | 'new_evidence' | 'fresh_intelligence' | 'social_activity' | 'dossier_refresh';
  eventLabel: string;
  eventTimestamp: number;
  headline: string;
  detail: string;
  sourcePath: string;
  sourceLabel: string;
  updatedAt: number;
}

export interface HomepageHeatResponse {
  generatedAt: number;
  logic: string[];
  items: HomepageHeatItem[];
}

export interface OfficialsFilters {
  level?: string;
  office_branch?: string;
  state?: string;
  party?: string;
  chamber?: string;
  search?: string;
  person_type?: string;
  has_dossier?: boolean;
  lifecycle_status?: OfficialLifecycleStatus;
  include_archived?: boolean;
  page?: number;
  limit?: number;
  sort?: OfficialSortOption;
}

export type OfficialSortOption =
  | 'name'
  | 'score_desc'
  | 'score_asc'
  | 'adoptions_desc'
  | 'state'
  | 'recent';

export type FacetValue = { value: string | null; count: number };

export interface FacetsResponse {
  parties: FacetValue[];
  states: FacetValue[];
  levels: FacetValue[];
  branches: FacetValue[];
  chambers: FacetValue[];
}

async function fetchJSON<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API error ${res.status}: ${body}`);
  }
  return res.json();
}

export async function fetchOfficials(filters: OfficialsFilters = {}): Promise<OfficialsListResponse> {
  const params = new URLSearchParams();
  if (filters.person_type) params.set('person_type', filters.person_type);
  if (filters.level) params.set('level', filters.level);
  if (filters.office_branch) params.set('office_branch', filters.office_branch);
  if (filters.state) params.set('state', filters.state);
  if (filters.party) params.set('party', filters.party);
  if (filters.chamber) params.set('chamber', filters.chamber);
  if (filters.search) params.set('search', filters.search);
  if (filters.has_dossier) params.set('has_dossier', 'true');
  if (filters.lifecycle_status) params.set('lifecycle_status', filters.lifecycle_status);
  if (filters.include_archived) params.set('include_archived', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.sort) params.set('sort', filters.sort);

  const qs = params.toString();
  return fetchJSON<OfficialsListResponse>(`${API_BASE}/officials${qs ? '?' + qs : ''}`);
}

export async function fetchFacets(filters: Omit<OfficialsFilters, 'page' | 'limit' | 'sort'> = {}): Promise<FacetsResponse> {
  const params = new URLSearchParams();
  if (filters.person_type) params.set('person_type', filters.person_type);
  if (filters.level) params.set('level', filters.level);
  if (filters.office_branch) params.set('office_branch', filters.office_branch);
  if (filters.state) params.set('state', filters.state);
  if (filters.party) params.set('party', filters.party);
  if (filters.chamber) params.set('chamber', filters.chamber);
  if (filters.search) params.set('search', filters.search);
  if (filters.has_dossier) params.set('has_dossier', 'true');
  if (filters.lifecycle_status) params.set('lifecycle_status', filters.lifecycle_status);
  if (filters.include_archived) params.set('include_archived', 'true');

  const qs = params.toString();
  return fetchJSON<FacetsResponse>(`${API_BASE}/officials/facets${qs ? '?' + qs : ''}`);
}

export async function fetchOfficialById(id: string): Promise<OfficialDetailResponse> {
  return fetchJSON<OfficialDetailResponse>(`${API_BASE}/officials/${encodeURIComponent(id)}`);
}

export async function fetchOfficialDetailBundle(id: string): Promise<OfficialDetailBundleResponse> {
  return fetchJSON<OfficialDetailBundleResponse>(`${API_BASE}/officials/${encodeURIComponent(id)}/detail-bundle`);
}

export async function fetchOfficialArchiveHistory(id: string): Promise<OfficialArchiveHistoryResponse> {
  return fetchJSON<OfficialArchiveHistoryResponse>(`${API_BASE}/officials/${encodeURIComponent(id)}/archive-history`);
}

export async function fetchStats(): Promise<StatsResponse> {
  return fetchJSON<StatsResponse>(`${API_BASE}/officials/stats/summary`);
}

export async function fetchHomepageHeat(limit = 6): Promise<HomepageHeatResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  return fetchJSON<HomepageHeatResponse>(`${API_BASE}/officials/heat/now?${params.toString()}`);
}

// Intelligence cache types
export interface DossierStatus {
  officialId: string;
  lifecycleStatus?: string;
  completenessSource?: 'live' | 'archived_snapshot';
  sections: Record<string, { cached: boolean; fresh: boolean; created_at: number; expires_at: number }>;
  expectedSections?: string[];
  completedSections?: number;
  missingSections?: string[];
  staleSections?: string[];
  coveragePct?: number;
  totalAssets: number;
  totalResearch: number;
  hasPhoto?: boolean;
  photoExpected?: boolean;
  photoMissing?: boolean;
}

export interface CacheEntry<T = unknown> {
  hit: boolean;
  officialId: string;
  cacheType: string;
  data?: T;
  created_at?: number;
  expires_at?: number;
  archived_snapshot?: boolean;
}

export interface CachedBriefing {
  text: string;
  generatedAt: number;
}

export interface CachedVotingRecord {
  votes: { bill: string; title: string; date: string; vote: string; result: string; category: string }[];
  analysis: string;
  generatedAt: number;
}

export interface CachedPublicRecord {
  text: string;
  findings: { title: string; date: string; source: string; impact_type: string; description: string }[];
  generatedAt: number;
}

export interface AccountabilityFlags {
  conflict_of_interest: number;
  flip_flop: number;
  corruption_investigation: number;
}

export interface CachedScore {
  score: number;
  summary: string;
  factors: string[];
  confidence?: number;
  flags?: AccountabilityFlags;
  generatedAt: number;
}

export interface CachedLegislativeRecord {
  officialId: string;
  state: string;
  billsSigned: number;
  billsVetoed: number;
  executiveOrders: number | null;
  recentActions: {
    billNumber: string;
    title: string;
    action: string;
    date: string;
  }[];
  source: string;
}

export interface CachedCampaignFinance {
  officialId: string;
  candidateId: string | null;
  candidateName: string;
  totalRaised: number | null;
  totalSpent: number | null;
  cashOnHand: number | null;
  debt: number | null;
  topContributors: {
    name: string;
    total: number;
    orgType: string;
  }[];
  topSectors: {
    sector: string;
    total: number;
    sectorId: string;
  }[];
  cycle: number;
  source: string;
}

export async function fetchDossierStatus(officialId: string): Promise<DossierStatus> {
  return fetchJSON<DossierStatus>(`${API_BASE}/intelligence/dossier/${encodeURIComponent(officialId)}`);
}

export async function fetchCachedIntelligence<T = unknown>(officialId: string, cacheType: string): Promise<CacheEntry<T>> {
  return fetchJSON<CacheEntry<T>>(`${API_BASE}/intelligence/cache/${encodeURIComponent(officialId)}/${encodeURIComponent(cacheType)}`);
}

export async function fetchCachedAssets(officialId: string): Promise<{ assets: any[] }> {
  return fetchJSON<{ assets: any[] }>(`${API_BASE}/intelligence/assets/${encodeURIComponent(officialId)}`);
}

export async function fetchResearchHistory(officialId: string): Promise<{ research: any[] }> {
  return fetchJSON<{ research: any[] }>(`${API_BASE}/intelligence/research/${encodeURIComponent(officialId)}`);
}

export function normalizePersonType(type: string | null | undefined): string {
  if (!type) return 'official';
  const value = type.toLowerCase();
  if (value === 'ceo' || value === 'executive') return 'corporate';
  return value;
}

export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' }, { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'VI', name: 'U.S. Virgin Islands' },
];

export function partyColor(party: string | null): string {
  if (!party) return 'text-slate-500';
  const p = party.toLowerCase();
  if (p === 'republican' || p === 'gop') return 'text-red-400';
  if (p === 'democrat' || p === 'democratic') return 'text-blue-400';
  if (p === 'independent') return 'text-purple-400';
  if (p === 'libertarian') return 'text-yellow-400';
  if (p === 'green') return 'text-green-400';
  return 'text-slate-400';
}

export function partyBg(party: string | null): string {
  if (!party) return 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
  const p = party.toLowerCase();
  if (p === 'republican' || p === 'gop') return 'bg-red-50 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800/50';
  if (p === 'democrat' || p === 'democratic') return 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-400 dark:border-blue-800/50';
  if (p === 'independent') return 'bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-400 dark:border-purple-800/50';
  return 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50';
}

export function levelBadge(level: string): string {
  if (level === 'federal') return 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-400 dark:border-amber-800/50';
  if (level === 'state') return 'bg-teal-50 text-teal-700 border-teal-300 dark:bg-teal-900/40 dark:text-teal-400 dark:border-teal-800/50';
  return 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50';
}

export function platformIcon(platform: string): string {
  const icons: Record<string, string> = {
    twitter: 'X', facebook: 'FB', instagram: 'IG', youtube: 'YT',
    bluesky: 'BS', threads: 'TH', mastodon: 'MD', website: 'WEB',
  };
  return icons[platform] || platform.toUpperCase().slice(0, 2);
}

export function personTypeBadge(type: string): string {
  const normalized = normalizePersonType(type);
  const map: Record<string, string> = {
    official: 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-400 dark:border-blue-800/50',
    corporate: 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900/40 dark:text-orange-400 dark:border-orange-800/50',
    donor: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800/50',
    lobbyist: 'bg-violet-50 text-violet-700 border-violet-300 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800/50',
    media: 'bg-cyan-50 text-cyan-700 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-400 dark:border-cyan-800/50',
  };
  return map[normalized] || 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50';
}

export function personTypeLabel(type: string): string {
  const normalized = normalizePersonType(type);
  const map: Record<string, string> = {
    official: 'Government Official',
    corporate: 'Corporate Executive',
    donor: 'Political Donor',
    lobbyist: 'Lobbyist',
    media: 'Media Figure',
    other: 'Other',
  };
  return map[normalized] || normalized;
}

export function impactBadge(impact: string | null): string {
  if (impact === 'negative') return 'bg-red-900/40 text-red-400 border-red-800/50';
  if (impact === 'positive') return 'bg-green-900/40 text-green-400 border-green-800/50';
  return 'bg-slate-800/60 text-slate-400 border-slate-700/50';
}

export const PERSON_TYPES = [
  { value: 'official', label: 'Government Officials' },
  { value: 'corporate', label: 'Corporate Executives' },
  { value: 'donor', label: 'Political Donors' },
  { value: 'lobbyist', label: 'Lobbyists' },
  { value: 'media', label: 'Media Figures' },
];

// --- Admin API functions (require ADMIN_API_KEY) ---

export interface AgentTask {
  id: string;
  official_id: string;
  agent_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result_summary: string | null;
  error_message: string | null;
  started_at: number | null;
  completed_at: number | null;
  created_at: number;
  official_name?: string;
  person_type?: string;
  party?: string;
  state?: string;
  photo_url?: string;
}

export interface AdminStats {
  totalOfficials: number;
  officialsWithDossier: number;
  cacheByType: Record<string, number>;
  tasksByStatus: Record<string, number>;
}

export interface BuildResult {
  status: string;
  officialId?: string;
  agents?: Record<string, string>;
  message?: string;
}

export interface BatchBuildResult {
  status: string;
  batchId: string;
  totalEnqueued: number;
  enqueued: Record<string, string[]>;
  skipped: Record<string, string>;
  message: string;
}

export interface BuildMissingRequest {
  person_type?: string;
  state?: string;
  party?: string;
  level?: string;
  search?: string;
  limit?: number;
  force?: boolean;
}

export interface ArchiveOfficialRequest {
  reason?: string;
  replaced_by_official_id?: string | null;
}

export interface ArchiveOfficialResponse {
  status: string;
  officialId: string;
  archivedAt: number;
  archiveSnapshotId?: string | null;
  archiveHistoryCount?: number;
  successorOfficialId?: string | null;
  replacedByOfficial?: ArchivedOfficialReference | null;
}

export interface UpdateOfficialSuccessorRequest {
  successorOfficialId?: string | null;
  replaced_by_official_id?: string | null;
}

export interface FreshnessCandidate {
  officialId: string;
  name: string;
  personType: string;
  missingSections: string[];
  staleSections: string[];
  missingPhoto: boolean;
}

export type SuccessorReviewIssueType = 'missing_successor' | 'orphan_successor';

export interface SuccessorReviewItem {
  officialId: string;
  officialName: string;
  source: string;
  state: string | null;
  officeName: string | null;
  chamber: string | null;
  district: string | null;
  archivedAt: number | null;
  archiveReason: string | null;
  successorOfficialId: string | null;
  issueType: SuccessorReviewIssueType;
}

export interface ArchiveSnapshotReviewItem {
  officialId: string;
  officialName: string;
  source: string;
  state: string | null;
  officeName: string | null;
  chamber: string | null;
  district: string | null;
  archivedAt: number | null;
  archiveReason: string | null;
  successorOfficialId: string | null;
  hasSuccessorLink: boolean;
}

export interface TurnoverPreviewItem {
  officialId: string;
  officialName: string;
  source: string;
  state: string | null;
  officeName: string | null;
  chamber: string | null;
  district: string | null;
  seatKey: string | null;
  reason: 'seat_replaced' | 'missing_from_roster';
  replacedByOfficialId: string | null;
  replacedByName: string | null;
}

export interface FreshnessStatusResponse {
  status: string;
  limit: number;
  totalCandidates: number;
  candidates: FreshnessCandidate[];
  archives: {
    totalArchivedOfficials: number;
    successorReviewTotal: number;
    successorReviewItems: SuccessorReviewItem[];
    snapshotBackfillReviewTotal: number;
    snapshotBackfillReviewItems: ArchiveSnapshotReviewItem[];
  };
}

export interface FreshnessTurnoverPreviewResponse {
  status: string;
  limit: number;
  generatedAt: number;
  congress: {
    total: number;
    linked: number;
    unlinked: number;
    items: TurnoverPreviewItem[];
    skipped?: string;
  };
  states: {
    total: number;
    linked: number;
    unlinked: number;
    items: TurnoverPreviewItem[];
    skipped?: string;
    states?: string[];
  };
}

export interface FreshnessRunRequest {
  limit?: number;
  dryRun?: boolean;
  force?: boolean;
  refreshOfficials?: boolean;
  backfillArchiveSnapshots?: boolean;
  processPasses?: number;
}

export interface FreshnessRunResult {
  status: string;
  startedAt: number;
  finishedAt: number;
  dryRun: boolean;
  limit: number;
  refreshOfficials: boolean;
  force: boolean;
  ingestion: {
    congress?: { processed: number; created: number; updated: number };
    states?: { processed: number; created: number; updated: number };
    archiveBackfill?: { processed: number; snapshotsCreated: number; officialIds: string[] };
  };
  preview: FreshnessCandidate[];
  totalCandidates: number;
  selectedOfficialIds: string[];
  batchId: string | null;
  totalEnqueued: number;
  enqueued: Record<string, string[]>;
  skipped: Record<string, string>;
  queueRuns: { processed: number; completed: number; failed: number; skipped?: string }[];
  notes: string[];
}

async function adminFetch<T>(url: string, adminKey: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...options?.headers,
      'X-Admin-Key': adminKey,
      'Content-Type': 'application/json',
    },
  });
  if (res.status === 401) throw new Error('Invalid admin key');
  if (res.status === 503) throw new Error('Service not configured');
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API error ${res.status}: ${body}`);
  }
  return res.json();
}

export async function adminVerifyKey(adminKey: string): Promise<boolean> {
  try {
    await adminFetch<AdminStats>(`${API_BASE}/intelligence/admin/stats`, adminKey);
    return true;
  } catch {
    return false;
  }
}

export async function adminFetchStats(adminKey: string): Promise<AdminStats> {
  return adminFetch<AdminStats>(`${API_BASE}/intelligence/admin/stats`, adminKey);
}

export async function adminFetchTasks(adminKey: string, status?: string, limit?: number): Promise<{ tasks: AgentTask[] }> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return adminFetch<{ tasks: AgentTask[] }>(`${API_BASE}/intelligence/tasks${qs ? '?' + qs : ''}`, adminKey);
}

export async function adminBuildDossier(adminKey: string, officialId: string, force = false): Promise<BuildResult> {
  return adminFetch<BuildResult>(`${API_BASE}/intelligence/build/${encodeURIComponent(officialId)}`, adminKey, {
    method: 'POST',
    body: JSON.stringify({ force }),
  });
}

export async function adminBuildBatch(adminKey: string, officialIds: string[], force = false): Promise<BatchBuildResult> {
  return adminFetch<BatchBuildResult>(`${API_BASE}/intelligence/build-batch`, adminKey, {
    method: 'POST',
    body: JSON.stringify({ officialIds, force }),
  });
}

export async function adminBuildMissingDossiers(adminKey: string, request: BuildMissingRequest): Promise<BatchBuildResult> {
  return adminFetch<BatchBuildResult>(`${API_BASE}/intelligence/build-missing`, adminKey, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function adminArchiveOfficial(adminKey: string, officialId: string, request: ArchiveOfficialRequest = {}): Promise<ArchiveOfficialResponse> {
  return adminFetch<ArchiveOfficialResponse>(`${API_BASE}/officials/${encodeURIComponent(officialId)}/archive`, adminKey, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function adminRestoreOfficial(adminKey: string, officialId: string): Promise<{ status: string; officialId: string; restoredAt: number }> {
  return adminFetch<{ status: string; officialId: string; restoredAt: number }>(`${API_BASE}/officials/${encodeURIComponent(officialId)}/restore`, adminKey, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function adminUpdateOfficialSuccessor(
  adminKey: string,
  officialId: string,
  request: UpdateOfficialSuccessorRequest,
): Promise<{
  status: string;
  officialId: string;
  successorOfficialId: string | null;
  updatedAt: number;
  replacedByOfficial: ArchivedOfficialReference | null;
}> {
  return adminFetch<{
    status: string;
    officialId: string;
    successorOfficialId: string | null;
    updatedAt: number;
    replacedByOfficial: ArchivedOfficialReference | null;
  }>(`${API_BASE}/officials/${encodeURIComponent(officialId)}/successor`, adminKey, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function adminDeleteDossier(adminKey: string, officialId: string): Promise<{ status: string; cacheDeleted: number; assetsDeleted: number; researchDeleted: number }> {
  return adminFetch<{ status: string; cacheDeleted: number; assetsDeleted: number; researchDeleted: number }>(
    `${API_BASE}/intelligence/dossier/${encodeURIComponent(officialId)}`, adminKey, { method: 'DELETE' },
  );
}

export async function adminPurgeExpired(adminKey: string): Promise<{ status: string; rowsAffected: number }> {
  return adminFetch<{ status: string; rowsAffected: number }>(`${API_BASE}/intelligence/cache/expired`, adminKey, {
    method: 'DELETE',
  });
}

export async function adminCancelTask(adminKey: string, taskId: string, reason?: string): Promise<{ status: string; taskId: string }> {
  return adminFetch<{ status: string; taskId: string }>(
    `${API_BASE}/intelligence/tasks/${encodeURIComponent(taskId)}/cancel`, adminKey,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

export async function adminRetryTask(adminKey: string, taskId: string): Promise<{ status: string; oldTaskId: string; newTaskId: string; result: string }> {
  return adminFetch<{ status: string; oldTaskId: string; newTaskId: string; result: string }>(
    `${API_BASE}/intelligence/tasks/${encodeURIComponent(taskId)}/retry`, adminKey,
    { method: 'POST' },
  );
}

export async function adminResolveStuckTasks(adminKey: string, staleMinutes = 10): Promise<{ status: string; tasksResolved: number; staleMinutes: number }> {
  return adminFetch<{ status: string; tasksResolved: number; staleMinutes: number }>(
    `${API_BASE}/intelligence/tasks/resolve-stuck`, adminKey,
    { method: 'POST', body: JSON.stringify({ stale_minutes: staleMinutes }) },
  );
}

export async function adminDeleteTask(adminKey: string, taskId: string): Promise<{ status: string; taskId: string }> {
  return adminFetch<{ status: string; taskId: string }>(
    `${API_BASE}/intelligence/tasks/${encodeURIComponent(taskId)}`, adminKey,
    { method: 'DELETE' },
  );
}

export async function adminRunMigration(adminKey: string): Promise<unknown> {
  return adminFetch<unknown>(`${API_BASE}/db/migrate`, adminKey, { method: 'POST' });
}

export async function adminFetchFreshnessStatus(adminKey: string, limit = 25): Promise<FreshnessStatusResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  return adminFetch<FreshnessStatusResponse>(`${API_BASE}/intelligence/admin/freshness/status?${params.toString()}`, adminKey);
}

export async function adminFetchTurnoverPreview(
  adminKey: string,
  options: { limit?: number; includeCongress?: boolean; includeStates?: boolean; states?: string[] } = {},
): Promise<FreshnessTurnoverPreviewResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 25));
  if (options.includeCongress === false) params.set('includeCongress', 'false');
  if (options.includeStates === false) params.set('includeStates', 'false');
  if (options.states && options.states.length > 0) params.set('states', options.states.join(','));
  return adminFetch<FreshnessTurnoverPreviewResponse>(`${API_BASE}/intelligence/admin/freshness/turnover-preview?${params.toString()}`, adminKey);
}

export async function adminRunFreshness(adminKey: string, request: FreshnessRunRequest): Promise<FreshnessRunResult> {
  return adminFetch<FreshnessRunResult>(`${API_BASE}/intelligence/admin/freshness/run`, adminKey, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

// --- Agent Configuration ---

export type AgentProvider = 'anthropic' | 'openai' | 'xai' | 'openrouter';
export type AgentCostProfileName = 'lowest_cost' | 'happy_medium' | 'highest_quality';

export interface AgentConfigEntry {
  provider: AgentProvider;
  model: string;
}

export interface AgentCostProfileEntry {
  key: AgentCostProfileName;
  label: string;
  description: string;
  configs: Record<string, AgentConfigEntry>;
}

export async function adminFetchAgentConfigs(adminKey: string): Promise<{ configs: Record<string, AgentConfigEntry> }> {
  return adminFetch<{ configs: Record<string, AgentConfigEntry> }>(`${API_BASE}/intelligence/admin/agent-config`, adminKey);
}

export async function adminFetchAgentConfigProfiles(adminKey: string): Promise<{ defaultProfile: AgentCostProfileName; profiles: Record<AgentCostProfileName, AgentCostProfileEntry> }> {
  return adminFetch<{ defaultProfile: AgentCostProfileName; profiles: Record<AgentCostProfileName, AgentCostProfileEntry> }>(`${API_BASE}/intelligence/admin/agent-config/profiles`, adminKey);
}

export async function adminApplyAgentConfigProfile(adminKey: string, profile: AgentCostProfileName): Promise<{ status: string; profile: AgentCostProfileName; defaultProfile: AgentCostProfileName; appliedCount: number; configs: Record<string, AgentConfigEntry> }> {
  return adminFetch<{ status: string; profile: AgentCostProfileName; defaultProfile: AgentCostProfileName; appliedCount: number; configs: Record<string, AgentConfigEntry> }>(
    `${API_BASE}/intelligence/admin/agent-config/apply-profile`, adminKey,
    { method: 'POST', body: JSON.stringify({ profile }) },
  );
}

export async function adminUpdateAgentConfig(
  adminKey: string, agentType: string, update: { provider?: string; model?: string },
): Promise<{ status: string; agentType: string; provider: string; model: string }> {
  return adminFetch<{ status: string; agentType: string; provider: string; model: string }>(
    `${API_BASE}/intelligence/admin/agent-config/${encodeURIComponent(agentType)}`, adminKey,
    { method: 'PUT', body: JSON.stringify(update) },
  );
}

// --- Queue Management ---

export interface QueueSettings {
  max_concurrent_officials: number;
  max_concurrent_agents: number;
  delay_between_officials_ms: number;
  paused: boolean;
}

export interface QueueStatusData {
  settings: QueueSettings;
  pending: number;
  running: number;
  completed_today: number;
  failed_today: number;
  officials_pending: number;
  officials_running: number;
  recent_batches: { batch_id: string; total: number; pending: number; running: number; completed: number; failed: number; created_at: number }[];
}

export async function adminFetchQueueStatus(adminKey: string): Promise<QueueStatusData> {
  return adminFetch<QueueStatusData>(`${API_BASE}/intelligence/admin/queue-status`, adminKey);
}

export async function adminUpdateQueueSettings(adminKey: string, updates: Partial<QueueSettings>): Promise<{ status: string; settings: QueueSettings }> {
  return adminFetch<{ status: string; settings: QueueSettings }>(
    `${API_BASE}/intelligence/admin/queue-settings`, adminKey,
    { method: 'PUT', body: JSON.stringify(updates) },
  );
}

export async function adminPauseQueue(adminKey: string): Promise<{ status: string }> {
  return adminFetch<{ status: string }>(`${API_BASE}/intelligence/admin/queue-pause`, adminKey, { method: 'POST' });
}

export async function adminResumeQueue(adminKey: string): Promise<{ status: string }> {
  return adminFetch<{ status: string }>(`${API_BASE}/intelligence/admin/queue-resume`, adminKey, { method: 'POST' });
}

export async function adminProcessQueue(adminKey: string): Promise<{ status: string; processed: number; completed: number; failed: number }> {
  return adminFetch<{ status: string; processed: number; completed: number; failed: number }>(
    `${API_BASE}/intelligence/admin/process-queue`, adminKey, { method: 'POST' },
  );
}

export async function adminDrainQueue(adminKey: string): Promise<{ status: string; tasksCancelled: number }> {
  return adminFetch<{ status: string; tasksCancelled: number }>(
    `${API_BASE}/intelligence/admin/queue-drain`, adminKey, { method: 'POST' },
  );
}

// --- Bluesky Integration ---

export interface BlueskyReply {
  handle: string;
  displayName: string;
  avatar: string | null;
  text: string;
  createdAt: string;
  likeCount: number;
  uri: string;
  replies: BlueskyReply[];
}

export interface BlueskyThreadData {
  hasPost: boolean;
  uri?: string;
  webUrl?: string | null;
  text?: string;
  likes?: number;
  reposts?: number;
  replies?: number;
  topReplies?: BlueskyReply[];
  createdAt?: number;
}

export async function adminPostToBluesky(adminKey: string, officialId: string): Promise<{ status: string; uri: string; cid: string; text: string; webUrl: string | null }> {
  return adminFetch<{ status: string; uri: string; cid: string; text: string; webUrl: string | null }>(
    `${API_BASE}/bluesky/post/${encodeURIComponent(officialId)}`, adminKey, { method: 'POST' },
  );
}

export async function fetchBlueskyThread(officialId: string): Promise<BlueskyThreadData> {
  return fetchJSON<BlueskyThreadData>(`${API_BASE}/bluesky/thread/${encodeURIComponent(officialId)}`);
}

export async function adminDeleteBlueskyPost(adminKey: string, officialId: string): Promise<{ status: string; rowsAffected: number }> {
  return adminFetch<{ status: string; rowsAffected: number }>(
    `${API_BASE}/bluesky/post/${encodeURIComponent(officialId)}`, adminKey, { method: 'DELETE' },
  );
}

// --- Contribution Review (Admin) ---

export interface ContributionReview {
  id: string;
  officialId: string;
  officialName: string;
  officialPhoto: string | null;
  officialType: string;
  contributionType: string;
  title: string;
  description: string;
  sourceUrl: string | null;
  evidenceUrls: string[];
  submissionDate: number | null;
  status: string;
  visibility: string;
  reviewNotes: string | null;
  upvotes: number;
  downvotes: number;
  createdAt: number;
  reviewedAt: number | null;
  contributor: {
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
}

export interface ContributionReviewStats {
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  total: number;
  pending: number;
}

export async function adminFetchContributions(
  adminKey: string, status = 'pending', limit = 100, offset = 0,
): Promise<{ contributions: ContributionReview[]; count: number; total: number }> {
  const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) });
  return adminFetch<{ contributions: ContributionReview[]; count: number; total: number }>(
    `${API_BASE}/contributions/admin/pending?${params}`, adminKey,
  );
}

export async function adminFetchContributionStats(adminKey: string): Promise<ContributionReviewStats> {
  return adminFetch<ContributionReviewStats>(`${API_BASE}/contributions/admin/stats`, adminKey);
}

export async function adminReviewContribution(
  adminKey: string, contributionId: string, status: 'approved' | 'rejected' | 'flagged', reviewNotes?: string,
): Promise<{ message: string; id: string; previousStatus: string; newStatus: string }> {
  return adminFetch<{ message: string; id: string; previousStatus: string; newStatus: string }>(
    `${API_BASE}/contributions/admin/${encodeURIComponent(contributionId)}/review`, adminKey,
    { method: 'PATCH', body: JSON.stringify({ status, reviewNotes }) },
  );
}

export interface NewsletterSummary {
  counts: {
    activeSubscribers: number;
    totalSubscribers: number;
    totalIssues: number;
    sentDeliveries: number;
    failedDeliveries: number;
  };
  latestIssue: {
    id: string;
    slug: string;
    title: string;
    subject: string;
    status: string;
    generatedAt: number | null;
    sentAt: number | null;
  } | null;
}

export interface NewsletterIssue {
  id: string;
  slug: string;
  status: string;
  title: string;
  subject: string;
  introText: string | null;
  htmlContent: string;
  textContent: string;
  summary: Record<string, unknown> | null;
  sourceWindowStart: number;
  sourceWindowEnd: number;
  refreshedAt: number | null;
  generatedAt: number | null;
  sentAt: number | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface NewsletterSubscriber {
  id: string;
  email: string;
  status: string;
  source: string | null;
  preferences: Record<string, unknown> | null;
  unsubscribeToken: string;
  confirmedAt: number | null;
  unsubscribedAt: number | null;
  lastSentIssueId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface NewsletterSendResult {
  issueId: string;
  mode: 'live' | 'test';
  attempted: number;
  sent: number;
  failed: number;
  skipped: number;
  deliveries: Array<{
    email: string;
    status: 'sent' | 'failed' | 'skipped';
    providerMessageId?: string | null;
    error?: string | null;
  }>;
}

export async function adminFetchNewsletterSummary(adminKey: string): Promise<NewsletterSummary> {
  return adminFetch<NewsletterSummary>(`${API_BASE}/newsletter/admin/summary`, adminKey);
}

export async function adminFetchNewsletterSubscribers(
  adminKey: string,
  status?: 'active' | 'unsubscribed',
  limit = 100,
): Promise<{ subscribers: NewsletterSubscriber[] }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set('status', status);
  return adminFetch<{ subscribers: NewsletterSubscriber[] }>(`${API_BASE}/newsletter/admin/subscribers?${params.toString()}`, adminKey);
}

export async function adminFetchNewsletterIssues(adminKey: string, limit = 10): Promise<{ issues: NewsletterIssue[] }> {
  return adminFetch<{ issues: NewsletterIssue[] }>(`${API_BASE}/newsletter/admin/issues?limit=${encodeURIComponent(String(limit))}`, adminKey);
}

export async function adminGenerateNewsletter(
  adminKey: string,
  body: { lookbackDays?: number; refreshBeforeGenerate?: boolean; processPasses?: number },
): Promise<{ status: string; issue: NewsletterIssue; freshness: FreshnessRunResult | null }> {
  return adminFetch<{ status: string; issue: NewsletterIssue; freshness: FreshnessRunResult | null }>(
    `${API_BASE}/newsletter/admin/generate`,
    adminKey,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function adminSendNewsletterTest(
  adminKey: string,
  issueId: string,
  email?: string,
): Promise<{ status: string } & NewsletterSendResult> {
  return adminFetch<{ status: string } & NewsletterSendResult>(
    `${API_BASE}/newsletter/admin/issues/${encodeURIComponent(issueId)}/test-send`,
    adminKey,
    { method: 'POST', body: JSON.stringify({ email }) },
  );
}

export async function adminRunWeeklyNewsletter(adminKey: string): Promise<{
  status: string;
  runId: string;
  issue: NewsletterIssue;
  send: NewsletterSendResult;
  freshness: {
    startedAt: number;
    finishedAt: number;
    totalCandidates: number;
    totalEnqueued: number;
    notes: string[];
  };
}> {
  return adminFetch(`${API_BASE}/newsletter/admin/run-weekly`, adminKey, { method: 'POST' });
}
